import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import { getDocs, collection, doc, updateDoc } from 'firebase/firestore';
import { firestore } from './lib/firebaseConfig';

// Temporary: Increment current_stage by 1 for all investments
window.incrementAllStages = async () => {
  const investments = await getDocs(collection(firestore, "investments"));
  const results = [];
  for (const inv of investments.docs) {
    const data = inv.data();
    const oldStage = data.current_stage || 0;
    const newStage = oldStage + 1;
    await updateDoc(doc(firestore, "investments", inv.id), { current_stage: newStage });
    results.push({ name: data.name, oldStage, newStage });
  }
  console.table(results);
  console.log('All investments updated!');
  return results;
};

// Temporary: Check unsold flats
window.checkUnsold = async (investmentName) => {
  const investments = await getDocs(collection(firestore, "investments"));
  for (const inv of investments.docs) {
    if (investmentName && !inv.data().name.toLowerCase().includes(investmentName.toLowerCase())) continue;
    console.log('Investment:', inv.data().name);
    const flats = await getDocs(collection(firestore, `investments/${inv.id}/flats`));
    const results = [];
    for (const flat of flats.docs) {
      const d = flat.data();
      if (d.status !== 'sold') {
        results.push({ code: d.code, status: d.status, size: d.size, type: d.type });
      }
    }
    if (results.length > 0) {
      console.table(results);
      console.log(`Found ${results.length} unsold flats`);
    } else {
      console.log('All flats are sold!');
    }
    return results;
  }
};

// Temporary: List all investments
window.listInvestments = async () => {
  const investments = await getDocs(collection(firestore, "investments"));
  const names = investments.docs.map(inv => ({ id: inv.id, name: inv.data().name }));
  console.table(names);
  return names;
};

// Check flat types and statuses for an investment
window.checkFlatTypes = async (investmentName) => {
  const investments = await getDocs(collection(firestore, "investments"));
  for (const inv of investments.docs) {
    if (investmentName && !inv.data().name.toLowerCase().includes(investmentName.toLowerCase())) continue;
    console.log('Investment:', inv.data().name, 'ID:', inv.id);
    const flats = await getDocs(collection(firestore, `investments/${inv.id}/flats`));
    const results = [];
    let soldWithResidential = 0;
    for (const flat of flats.docs) {
      const d = flat.data();
      results.push({ code: d.code, type: d.type, status: d.status, an_amount: d.an_amount });
      if (d.type?.toLowerCase() === 'residential' && d.status === 'sold') soldWithResidential++;
    }
    console.table(results);
    // Summary
    const typeCounts = {};
    const statusCounts = {};
    for (const r of results) {
      typeCounts[r.type || 'undefined'] = (typeCounts[r.type || 'undefined'] || 0) + 1;
      statusCounts[r.status || 'undefined'] = (statusCounts[r.status || 'undefined'] || 0) + 1;
    }
    console.log('Type counts:', typeCounts);
    console.log('Status counts:', statusCounts);
    console.log('Sold + residential (should show in table):', soldWithResidential);
    return { results, typeCounts, statusCounts, soldWithResidential };
  }
};

// Calculate and populate payment summary fields for all flats in an investment
window.calculatePaymentSummaries = async (investmentName) => {
  const investments = await getDocs(collection(firestore, "investments"));
  let totalUpdated = 0;

  for (const inv of investments.docs) {
    if (investmentName && !inv.data().name.toLowerCase().includes(investmentName.toLowerCase())) continue;

    console.log('Processing investment:', inv.data().name);
    const flats = await getDocs(collection(firestore, `investments/${inv.id}/flats`));

    for (const flat of flats.docs) {
      const flatData = flat.data();
      if (flatData.status !== 'sold') continue;

      // Get all payments for this flat
      const payments = await getDocs(collection(firestore, `investments/${inv.id}/flats/${flat.id}/payments`));

      let paidAmount = 0;
      let mandatoryAmount = 0;

      for (const payment of payments.docs) {
        const p = payment.data();
        mandatoryAmount += p.amount || 0;
        if (p.payment_status === 'Zapłacono') {
          paidAmount += p.amount || 0;
        } else {
          paidAmount += p.paidAmount || 0;
        }
      }

      const anAmount = flatData.an_amount || 0;
      const missingAmount = mandatoryAmount - paidAmount;
      const leftAmount = anAmount - paidAmount;

      // Update flat with calculated fields
      await updateDoc(doc(firestore, `investments/${inv.id}/flats/${flat.id}`), {
        paid_amount: paidAmount,
        mandatory_amount: mandatoryAmount,
        missing_amount: missingAmount > 0 ? missingAmount : 0,
        left_amount: leftAmount > 0 ? leftAmount : 0
      });

      console.log(`  ${flatData.code}: paid=${paidAmount}, mandatory=${mandatoryAmount}, missing=${missingAmount}, left=${leftAmount}`);
      totalUpdated++;
    }
  }

  console.log(`Done! Updated ${totalUpdated} flats.`);
  return totalUpdated;
};

// Find customer by name and their flats
window.findCustomer = async (name) => {
  const customers = await getDocs(collection(firestore, "customers"));
  const results = [];
  for (const cust of customers.docs) {
    const d = cust.data();
    const fullName = `${d.name || ''} ${d.surname || ''}`.toLowerCase();
    const buyerNames = d.buyers?.map(b => b.name?.toLowerCase()) || [];
    if (fullName.includes(name.toLowerCase()) || buyerNames.some(n => n?.includes(name.toLowerCase()))) {
      // Find their flats
      const flats = d.purchasedFlats || [];
      results.push({
        customerId: cust.id,
        name: d.name,
        surname: d.surname,
        buyers: d.buyers,
        flats: flats
      });
    }
  }
  console.log('Found customers:', results);

  // Get flat details
  for (const r of results) {
    for (const f of r.flats) {
      try {
        const flatDoc = await getDocs(collection(firestore, `investments/${f.investmentId}/flats`));
        for (const flat of flatDoc.docs) {
          if (flat.id === f.flatId) {
            console.log(`Flat: ${flat.data().code}, Investment: ${f.investmentId}`);
            r.flatCode = flat.data().code;
            r.flatData = flat.data();
          }
        }
      } catch (e) {
        console.error('Error fetching flat:', e);
      }
    }
  }
  return results;
};

// Compare sample sold flats from different investments
window.compareFlats = async () => {
  const investments = await getDocs(collection(firestore, "investments"));
  const samples = {};
  for (const inv of investments.docs) {
    const name = inv.data().name;
    const flats = await getDocs(collection(firestore, `investments/${inv.id}/flats`));
    // Find first sold flat
    for (const flat of flats.docs) {
      const d = flat.data();
      if (d.status === 'sold') {
        samples[name] = { id: flat.id, ...d };
        break;
      }
    }
  }
  console.log('=== SAMPLE SOLD FLATS FROM EACH INVESTMENT ===');
  for (const [name, flat] of Object.entries(samples)) {
    console.log(`\n--- ${name} ---`);
    console.log('Fields:', Object.keys(flat).sort().join(', '));
    console.log('Data:', flat);
  }
  // Show field comparison
  const allFields = new Set();
  Object.values(samples).forEach(flat => Object.keys(flat).forEach(k => allFields.add(k)));
  console.log('\n=== FIELD COMPARISON ===');
  const comparison = {};
  for (const field of [...allFields].sort()) {
    comparison[field] = {};
    for (const [name, flat] of Object.entries(samples)) {
      comparison[field][name] = flat[field] !== undefined ? '✓' : '✗';
    }
  }
  console.table(comparison);
  return samples;
};

// Populate total_size for all investments based on their flats
window.populateTotalSizes = async () => {
  const investments = await getDocs(collection(firestore, "investments"));
  const results = [];
  for (const inv of investments.docs) {
    const data = inv.data();
    const flats = await getDocs(collection(firestore, `investments/${inv.id}/flats`));
    const totalSize = flats.docs.reduce((sum, flat) => sum + (flat.data().size || 0), 0);
    await updateDoc(doc(firestore, "investments", inv.id), { total_size: totalSize });
    results.push({ name: data.name, total_size: totalSize, flats_count: flats.docs.length });
  }
  console.table(results);
  console.log('All investments updated with total_size!');
  return results;
};

// Temporary: Check flats with <60% paid
window.checkFlats = async (investmentName) => {
  const investments = await getDocs(collection(firestore, "investments"));
  for (const inv of investments.docs) {
    if (investmentName && !inv.data().name.toLowerCase().includes(investmentName.toLowerCase())) continue;
    console.log('Investment:', inv.data().name);
    const flats = await getDocs(collection(firestore, `investments/${inv.id}/flats`));
    const results = [];
    for (const flat of flats.docs) {
      const d = flat.data();
      if (d.status !== 'sold') continue;

      // Get all payments for this flat
      const payments = await getDocs(collection(firestore, `investments/${inv.id}/flats/${flat.id}/payments`));
      let totalPaid = 0;
      for (const payment of payments.docs) {
        const p = payment.data();
        // If status is "Zapłacono", count full amount as paid
        if (p.payment_status === 'Zapłacono') {
          totalPaid += p.amount || 0;
        } else {
          totalPaid += p.paidAmount || 0;
        }
      }

      const pct = d.an_amount > 0 ? (totalPaid / d.an_amount) * 100 : 0;
      if (pct < 50) results.push({ code: d.code, paid: pct.toFixed(1) + '%', an_amount: d.an_amount, totalPaid: totalPaid });
    }
    if (results.length > 0) {
      console.table(results.sort((a,b) => parseFloat(a.paid) - parseFloat(b.paid)));
    } else {
      console.log('All flats have >= 60% paid');
    }
    return results;
  }
};

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
